import {useMutation} from '@tanstack/react-query'

import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {ProviderDetail, ProviderListSchemaResponse, ProviderSchemaResponse} from '../domain'
import {useProvider} from '../hook'
import {API} from 'src/feature/bite/domain'
import {useAuthUser} from 'react-auth-kit'
import {useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {getTimeZone} from 'src/feature/bite/helpers/date'

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = useProvider()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['membersAll'],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = ProviderListSchemaResponse.safeParse(Data)
      const parse = ProviderListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        Error(parse.responseResult.exceptionDetail || errorMessage)
        return []
      }

      if (!safeParse.success) {
        Error(safeParse.error.message)
        return []
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

export function useUpdateProvider() {
  const auth = useAuthUser()
  const {headers, errors, signOut} = useRequestParams()

  const {detailSet} = useProvider()
  const merchantID = auth()?.merchantID
  const userID = auth()?.id

  return useMutation(
    ['ProviderUpdate'],
    async (values: any) => {
      values.certifications = values.certificates

      const Response = await fetch(`${API}/Provider/UpdateProvider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          userID,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.provider.create_error)
        return
      }

      Success(errors.provider.create_success)

      const timeZoneOffset = getTimeZone()

      const ResponseProviderDetail = await fetch(`${API}/Provider/GetProvider`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          merchantID,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const responseProviderDetail: ProviderDetail = await ResponseProviderDetail.json()

      const parseProviderDetail = ProviderSchemaResponse.parse(responseProviderDetail)

      if (parseProviderDetail.responseResult.result === false) {
        toast.error(parseProviderDetail.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      // set not existing days into workingHourList

      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const workingHourList = parseProviderDetail.response.workingHourList

      days.forEach((day, index) => {
        // eslint-disable-next-line eqeqeq
        if (!workingHourList.find((item) => item.day == index)) {
          const indexStart = days.indexOf(day).toString()

          workingHourList.push({
            day: indexStart,
            workingStartHour: '',
            workingEndHour: '',
          })
        }
      })

      workingHourList.sort((a, b) => a.day - b.day)

      parseProviderDetail.response.workingHourList = workingHourList

      // End: Set Working Hour List

      detailSet(parseProviderDetail.response)

      return parseProviderDetail.response
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.product.update_error)
      },
    }
  )
}
